<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const { t } = useI18n();
const store = useStore();

const isFullSize = ref(true);
const index = ref(0);

// Props
const props = defineProps({
    conflicts: {
        type: Array,
        required: true,
        default: () => []
    }
});

// Computed
const conflicts = computed(() => {
    return props.conflicts.filter(conflict => !conflict.hidden);
});
const zIndex = computed(() => {
    return conflicts.value.reduce((maxZIndex, currentConflict) => {
        return Math.max(maxZIndex, currentConflict.zIndex || 50);
    }, 50);
});
const conflict = computed(() => {
    return conflicts.value[index.value];
});

// Methods
// const determineStudio = (descriptionAsHtml) => {
//     if (!descriptionAsHtml) {
//         return null;
//     }

//     // Get the text from the HTML
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(descriptionAsHtml, 'text/html');
//     const text = doc.body.textContent || "";

//     const studioMatch = text.match(/^(affiliated with|belongs? to|belonging to|founder of|employed at) (.*?)(?!during|at|when)([.,] |[.,]$|$)/mi);

//     if (studioMatch) {
//         return studioMatch[2].trim();
//     }

//     return null;
// };
const highlight = () => {
    const staff = conflict.value.staff;
    const episode = conflict.value.episode;

    if (staff.isHighlighted) {
        return;
    }

    if (store.state.selectedEpisode !== episode) {
        store.commit('selectEpisode', {episode: episode, scroll: true});
    }

    staff.isHighlighted = true;

    nextTick(() => {
        if (document.querySelector('.highlighted')) {
            const y = document.querySelector('.highlighted').getBoundingClientRect().top
                + window.pageYOffset
                - window.innerHeight / 2;
            window.scrollTo({top: y});
        }
    });

    setTimeout(() => {
        delete staff.isHighlighted;
    }, 1500);
}
const select = (staff) => {
    if (conflict.value.value && conflict.value.value._select_id === staff._select_id) {
        conflict.value.value = null;
        return;
    }

    conflict.value.value = staff;
};
const save = () => {
    conflict.value.callback(conflict.value.value);
    conflict.value.hidden = true;

    if (conflicts.value.length === index.value) {
        index.value -= 1;
    }
};
const ignore = () => {
    conflict.value.callback(null);
    conflict.value.value = null;
    conflict.value.hidden = true;

    if (conflicts.value.length === index.value) {
        index.value -= 1;
    }
};

const getName = (staff) => {
    if (staff.id) {
        return staff.id;
    } else if (staff.ja) {
        return `ja:${staff.ja}`;
    } else if (staff.en) {
        return `en:${staff.en}`;
    } else {
        return;
    }
}
const getLink = (staff) => {
    if (staff.from == 'AL') {
        return `https://anilist.co/${staff.isStudio ? 'studio' : 'staff'}/${staff.id}`;
    }
    return `/${staff.isStudio ? 'studio' : 'person'}/${getName(staff)}`;
}
const getImage = (staff) => {
    if (staff?.raw?.avatar && staff?.id) {
        const prefix = staff.isStudio ? '0' : '1';
        return `/assets/img/staff/${prefix}-${staff.id}/${staff.raw.avatar}.jpg`;
    }
    return '/assets/img/kf.jpg';
}
const getStudios = (staff) => {
    const studios = staff?.studios || staff?.raw?.studios;
    if (!studios || studios.length === 0) {
        return null;
    }
    
    const keys = Object.keys(studios);
    const seen = new Set();
    return keys.filter(key => {
        const lower = key.toLowerCase();
        if (seen.has(lower)) {
            return false;
        }
        seen.add(lower);
        return true;
    }).join(', ');
}
const getJobs = (staff) => {
    const jobs = staff?.jobs || staff?.raw?.jobs;
    if (!jobs || jobs.length === 0) {
        return null;
    }
    return jobs.join(', ');
}
</script>

<template>
    <div class="fixed top-0 left-0 p-10 w-full h-full flex items-end bg-gradient-to-t pointer-events-none"
        :style="{ zIndex }"
        :class="[isFullSize ? 'from-black/50 to-60%' : 'from-black/20 to-10%']"
    >
        <div class="w-full text-center shadow-2xl pointer-events-auto">
            <div
                class="absolute w-16 right-16 bg-app-surface1 text-gray-400 rounded-t-md p-1 cursor-pointer"
                :class="[isFullSize ? '-translate-y-full' : 'mt-2']"
                @click="isFullSize = !isFullSize"
            >
                <font-awesome-icon icon="fa-solid fa-angles-down" v-if="isFullSize" />
                <font-awesome-icon icon="fa-solid fa-angles-up" v-else />
            </div>
            <div class="dialog bg-app-surface1 p-10 rounded-md overflow-hidden overflow-y-auto" v-if="isFullSize">
                <div class="flex items-center justify-between mb-4">
                    <button @click="index--" :disabled="index === 0" class="btn btn-default" v-if="conflicts.length > 1">
                        <font-awesome-icon icon="fa-solid fa-chevron-left" />
                    </button>

                    <h6 class="flex-1 font-inter tracking-wider text-gray-400 text-sm">
                        {{ t('notifications.conflicts.resolution') }} ({{ index + 1 }} / {{ conflicts.length }})
                    </h6>

                    <button @click="index++" :disabled="index === conflicts.length - 1" class="btn btn-default"
                        v-if="conflicts.length > 1">
                        <font-awesome-icon icon="fa-solid fa-chevron-right" />
                    </button>
                </div>

                <p class="text-left break-words">
                    <i18n-t keypath="notifications.conflicts.autoTranslationNeedsRevision" tag="span">
                        <template #name>
                            <strong>{{ conflict.origin }}</strong>
                        </template>
                        <template #highlightButton>
                            <button class="!inline !text-xs !px-2 !py-1.5 btn btn-xs btn-keyframe" @click="highlight">
                                Highlight row
                            </button>
                            <br />
                        </template>
                        <template #roles>
                            <em>{{ conflict.roleNames || t('notifications.conflicts.unknown') }}</em>
                        </template>
                    </i18n-t>
                    <!-- <template v-if="!isAniList">
                        <br />{{ t('notifications.conflicts.autoTranslationKfsl') }}
                    </template> -->
                </p>

                <div class="flex flex-wrap w-full my-4 gap-2">
                    <button v-for="staff of conflict.data" :key="staff.id"
                        class="flex flex-row gap-3 p-4 items-center rounded-md shadow hover:shadow-md focus:shadow-md transition-shadow duration-100 group"
                        :class="[conflict.value && conflict.value._select_id === staff._select_id ? 'is-active bg-ocean-400 text-white' : 'bg-app-surface0']"
                        @click="select(staff)">
                        <img :src="getImage(staff)" :alt="staff.name?.full || staff.en || staff.ja" class="w-12 h-12 object-cover rounded-md">
                        <div class="text-left">
                            <!-- Name -->
                            <p>
                                <span :class="{'font-bold': staff.isStudio}">{{ staff.name?.full || staff.en || staff.ja || staff.pn.en || staff.pn.ja }}</span>

                                <span v-if="staff.pn && (staff.pn.en || staff.pn.ja)?.trim() && (staff.pn.en || staff.pn.ja) != (staff.name?.full || staff.en || staff.ja)"> ({{ staff.pn.en || staff.pn.ja }})</span>
                            </p>
                            <div v-if="getStudios(staff) && !staff.isStudio" class="text-xs italic text-gray-400 group-[.is-active]:text-gray-200 leading-tight max-w-64 truncate" :title="getStudios(staff)">
                                ({{ getStudios(staff) }})
                            </div>
                            <div v-if="getJobs(staff)" class="mt-1 text-xs text-gray-500 group-[.is-active]:text-gray-200 leading-tight font-normal">
                                <strong>Jobs:</strong> {{ getJobs(staff) }}
                            </div>
                        </div>
                        <div class="flex-1 text-ocean-400 group-[.is-active]:text-white text-right ml-1">
                            <a :href="getLink(staff)" @click.stop
                                class="text-2xl" target="_blank">
                                <font-awesome-icon icon="fa-solid fa-at" />
                            </a>
                        </div>
                    </button>
                </div>

                <div class="flex gap-2 justify-center">
                    <button class="btn btn-primary" @click="save" :disabled="!conflict.value">{{ t('common.confirm') }}</button>
                    <button class="btn btn-default" @click="ignore">{{ t('common.ignore') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>